import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import styled from 'styled-components'
import { LinearGradient } from '../../../../elements/images/LinearGradient/index.js'
import { SpriteImage } from '../../../../elements/images/SpriteImage/SpriteImage.js'
import { Text } from '../../../../elements/typography/index.js'
import { useLayout } from '../../../../hooks/index.js'
import { formatTime } from '../../../../utils/secondsToDuration.js'
import { useSeekTime } from '../../Player/SeekTimeContext/SeekTimeContext.js'
import { ProgressBar } from '../ProgressBar/ProgressBar.js'
import { getSegmentFromImageStream } from './manifestParser/getSegmentFromImageStream.js'

const TrickPlayContainer = styled(View)`
  position: absolute;
  left: 0;
  top: -${(props) => props.imageHeight + props.theme.spacing.large}px;
  border: #fff solid 2px;
  border-radius: 3px;
`

export const TrickPlayImage = styled(SpriteImage)`
  border-radius: 3px;
`

const TimeText = styled(Text)`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
`

const HoverMarker = styled(View)`
  background-color: white;
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: ${(props) => props.position}px;
  width: 2px;
  z-index: 200;
`
// z-index to go below the progress bar handle

export const TrickPlayProgressBar = ({
  trickPlayOnScrub,
  showHoverMarker,
  onSlidingChange,
  onSlidingComplete,
  ...props
}) => {
  const { onLayout: onContainerLayout, width: overallWidth } = useLayout()
  const { onLayout: onProgressBarLayout, width: progressBarWidth } = useLayout()
  const [mouseSeekTime, setMouseSeekTime] = useState(null)
  const [cursorX, setCursorX] = useState(null)

  const imageExtrusion = (overallWidth - progressBarWidth) / 2

  const { seekTime, totalTime, trickPlayStream } = useSeekTime()

  const segment = getSegmentFromImageStream(trickPlayStream, mouseSeekTime)

  const aspectRatio = trickPlayStream?.resolution.width
    ? trickPlayStream?.resolution.width / trickPlayStream?.resolution.height
    : 0
  const imageWidth = Math.min(trickPlayStream?.resolution.width ?? 0, overallWidth)
  const imageHeight = imageWidth / aspectRatio

  const imageCenter = imageWidth / 2
  const minPos = -imageExtrusion + 5
  const maxPos = overallWidth - imageWidth - imageExtrusion - 10

  const imageOffset = Math.max(minPos, Math.min(maxPos, cursorX - imageCenter))

  const updateTrickPlayPosition = (seekTime, xOffset) => {
    setMouseSeekTime(seekTime)
    setCursorX(xOffset)
  }
  const clearTrickPlay = () => {
    setMouseSeekTime(null)
    setCursorX(null)
  }

  return (
    <View onLayout={onContainerLayout}>
      <ProgressBar
        value={props.seekTime ?? seekTime}
        totalTime={totalTime}
        thickOnHover
        {...props}
        onLayout={onProgressBarLayout}
        onMouseMove={updateTrickPlayPosition}
        onMouseLeave={clearTrickPlay}
        onSlidingChange={(...args) => {
          trickPlayOnScrub && updateTrickPlayPosition(...args)
          onSlidingChange?.(...args)
        }}
        onSlidingComplete={(...args) => {
          trickPlayOnScrub && clearTrickPlay()
          onSlidingComplete?.(...args)
        }}
      >
        {segment && (
          <TrickPlayContainer imageHeight={imageHeight} style={{ left: imageOffset }}>
            <TrickPlayImage
              {...segment}
              {...trickPlayStream?.layout}
              {...trickPlayStream?.resolution}
            />
            <LinearGradient
              colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0)', 'rgba(18, 18, 18, 1)']}
              locations={[0, 0.8, 1]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            <TimeText textAlign="center">{formatTime(mouseSeekTime, true)}</TimeText>
          </TrickPlayContainer>
        )}
        {showHoverMarker && cursorX !== null && (
          <HoverMarker position={cursorX} pointerEvents="none" />
        )}
      </ProgressBar>
    </View>
  )
}
